/**
 * **Raw Data's filters, in its URL — RD-20, `docs/EDITS-2026-09-24.md` §5.**
 *
 * Every filter key `/raw-data` understands, and the one function that builds a
 * link *into* it. The click-throughs on Stories and Spotlights (ST-21) call
 * `rawDataLink` rather than assembling a query string, so the two ends cannot
 * disagree about a parameter name.
 *
 * **These filters live on Raw Data only.** Stories, Spotlights and Channel
 * Overview never read them: a filtered story table with no visible reason reads
 * as missing data. The click-through is one-way, into Raw Data.
 *
 * `channel`, singular, is deliberately not the dashboards' `channels`: Raw Data
 * has its own channel filter, and the sidebar does not carry either between
 * pages.
 */
import type { RawFilters } from './api'

export const RAW_FILTER_KEYS = [
  'channel',
  'asset_type',
  'assets',
  'metric',
  'dimension',
  'status',
  'match_status',
  'classification',
  'author',
  // ST-27: ClickUp's Duration, by label, on ClickUp Tasks.
  'air_time',
  'episode',
  'name',
  'task',
] as const

/** How many stories one click-through may carry. Also a URL-length guard. */
export const MAX_SELECTED_ASSETS = 50

export function readRawFilters(params: URLSearchParams): RawFilters {
  const filters: RawFilters = {}
  for (const key of RAW_FILTER_KEYS) {
    const values = params
      .getAll(key)
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter(Boolean)
    if (values.length) filters[key] = [...new Set(values)]
  }
  return filters
}

/** Replaces every Raw Data filter on `params` with `filters`, leaving the rest
 *  (dataset, period, dates) alone. */
export function writeRawFilters(params: URLSearchParams, filters: RawFilters): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of RAW_FILTER_KEYS) next.delete(key)
  for (const [key, values] of Object.entries(filters)) {
    if (values.length) next.set(key, values.join(','))
  }
  return next
}

export function rawDataLink(p: {
  dataset: string
  filters: RawFilters
  from?: string
  to?: string
}): string {
  const params = writeRawFilters(new URLSearchParams({ dataset: p.dataset }), p.filters)
  if (p.from && p.to) {
    params.set('period', 'custom')
    params.set('from', p.from)
    params.set('to', p.to)
  }
  return `/raw-data?${params}`
}

/**
 * The landing for selected stories (RD-20 step 2): Asset Daily Stats, the
 * stories and their channels as chips, **from the earliest selected publish
 * date to today** — not the page's range, so a story picked under Lifetime does
 * not land on an empty 30-day window.
 */
export function storiesLink(
  rows: {
    asset_id: string
    handle: string
    published_at: string | null
    chosen_task_id?: string | null
    task_publish_date?: string | null
  }[],
  today: string,
): string {
  const picked = rows.slice(0, MAX_SELECTED_ASSETS)
  const earliest = picked
    .map((row) => row.published_at?.slice(0, 10))
    .filter((day): day is string => Boolean(day))
    .sort()[0]
  const tasks = matchedTasks(picked)
  // ST-28: a linked saved story's task is dated from its public story, days
  // or weeks before the post, so the range must reach back to it too.
  const earliestTask = picked
    .filter((row) => row.chosen_task_id)
    .map((row) => row.task_publish_date?.slice(0, 10))
    .filter((day): day is string => Boolean(day))
    .sort()[0]
  // A day earlier when a task travels with the link: a matched task may be
  // dated the day before its post (the matcher's ±1 tolerance), and the
  // ClickUp tab reads the same range.
  const from =
    earliest && tasks.length
      ? [shift(earliest, -1), earliestTask].filter((day): day is string => Boolean(day)).sort()[0]
      : earliest
  return rawDataLink({
    dataset: 'asset_stats_daily',
    filters: postFilters(picked),
    from,
    to: earliest ? today : undefined,
  })
}

function matchedTasks(rows: { chosen_task_id?: string | null }[]): string[] {
  return [...new Set(rows.map((row) => row.chosen_task_id).filter((id): id is string => Boolean(id)))]
}

/**
 * The filters every link out of a post carries (RD-22): the posts, their
 * channels, **and the ClickUp tasks they matched**. On a Snap dataset the task
 * filter shows as *not applicable here*; switch to ClickUp Tasks, Matches or
 * Candidates and it narrows to exactly those tasks, instead of every task.
 */
function postFilters(
  rows: { asset_id: string; handle: string; chosen_task_id?: string | null }[],
): RawFilters {
  const filters: RawFilters = {
    assets: rows.map((row) => row.asset_id),
    channel: [...new Set(rows.map((row) => row.handle))],
  }
  const tasks = matchedTasks(rows)
  if (tasks.length) filters.task = tasks
  return filters
}

// ---------------------------------------------------------------------------
// Relevant pages from relevant clicks (RD-21, 25 Sep)
// ---------------------------------------------------------------------------
//
// A click opens the dataset that can answer the question the clicked table was
// asking. The rule, in one place so every table follows it:
//
// | Clicked from | Opens |
// |---|---|
// | a post under a **date range** | Asset Daily Stats, publish date → today |
// | a post under **Snap Lifetime** | Asset Lifetime Stats — the figures on screen were lifetime |
// | a **saved story**, any view | Asset Lifetime Stats — per-post daily saved-story figures are unreliable (CLAUDE.md), and Snap omits some posts from them entirely |
// | a channel-day (Channel Daily) | Channel Daily Stats, those channels and days |
// | a channel under Snap Lifetime | Channel Lifetime Stats |
//
// Asset Lifetime Stats has no date column, so no range is sent with it.

export type PostKind = 'STORY' | 'SAVED_STORY' | 'SPOTLIGHT'
export type Window = 'range' | 'lifetime'

interface Post {
  asset_id: string
  handle: string
  published_at: string | null
  chosen_task_id?: string | null
  task_publish_date?: string | null
}

/** Which dataset a post click should open. */
export function postDataset(kind: PostKind, window: Window): 'asset_stats_daily' | 'asset_stats_lifetime' {
  return window === 'lifetime' || kind === 'SAVED_STORY' ? 'asset_stats_lifetime' : 'asset_stats_daily'
}

/** The link for one or more posts, following the rule above. */
export function postsLink(
  rows: Post[],
  context: { kind: PostKind; window: Window; today: string },
  override?: 'asset_stats_daily' | 'asset_stats_lifetime' | 'assets' | 'story_matches' | 'match_candidates',
): string {
  const dataset = override ?? postDataset(context.kind, context.window)
  if (dataset === 'asset_stats_daily') return storiesLink(rows, context.today)
  const picked = rows.slice(0, MAX_SELECTED_ASSETS)
  // Assets is date-filtered on publish date, so it needs a range spanning the
  // picked posts, or an older post drops out of the default window. The other
  // three have no date column and take none.
  const days = picked
    .map((row) => row.published_at?.slice(0, 10))
    .filter((day): day is string => Boolean(day))
    .sort()
  const dated = dataset === 'assets' && days.length > 0
  const filters = postFilters(picked)
  // Tasks Considered exists to show every competing task; narrowing it to the
  // one chosen would hide exactly what it is for.
  if (dataset === 'match_candidates') delete filters.task
  // 🔴 **A task that travels with the link brings its own date** (13 Oct). The
  // ClickUp tab reads the link's range, so with none — Lifetime, the match —
  // it fell back to Last 30 Days, and from Post Details to the post's day; a
  // linked saved story's task (ST-28) is dated weeks earlier and vanished.
  // A dataset with no date column ignores the range, so this costs nothing.
  const taskDays = filters.task
    ? picked
        .filter((row) => row.chosen_task_id)
        .map((row) => row.task_publish_date?.slice(0, 10))
        .filter((day): day is string => Boolean(day))
        .sort()
    : []
  if (taskDays.length) {
    return rawDataLink({
      dataset,
      filters,
      from: [taskDays[0]!, days[0]].filter((day): day is string => Boolean(day)).sort()[0],
      to: dated ? [days.at(-1)!, taskDays.at(-1)!].sort().at(-1) : context.today,
    })
  }
  return rawDataLink({
    dataset,
    filters,
    from: dated ? days[0] : undefined,
    to: dated ? days.at(-1) : undefined,
  })
}

/** Exactly the ClickUp task a post matched (RD-22). Channel + episode # would
 *  also return every sibling sharing that episode number — the saved-story
 *  subtask, re-runs — so the task id is what a click sends. */
export function taskLink(
  taskId: string,
  publishedAt?: string | null,
  taskDate?: string | null,
): string {
  // 🔴 **An id link must carry a range that contains what it points at.**
  // ClickUp Tasks is date-filtered, so a bare `?task=` fell back to Raw
  // Data's default Last 30 Days and a task from August showed 0 rows.
  // 🔴 **Dated from the task, not the post** (13 Oct): the post's ±1 day held
  // while every match was by date, but a saved story linked to its public
  // story (ST-28) holds a task dated days earlier — 19 Aug's link searched
  // 18–20 Aug for a task dated 16 Aug and showed nothing. The post's date is
  // only the fallback for a task with no date of its own.
  const day = (taskDate ?? publishedAt)?.slice(0, 10)
  return rawDataLink({
    dataset: 'clickup_tasks',
    filters: { task: [taskId] },
    from: day ? shift(day, -1) : undefined,
    to: day ? shift(day, 1) : undefined,
  })
}

function shift(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

/**
 * Channel-days from Channel Daily. One range covers every selected day, so a
 * selection of 3 Aug and 20 Aug opens 3–20 Aug for those channels — the bar
 * says so rather than implying only the ticked days come back.
 */
export function channelDaysLink(rows: { handle: string; stat_date: string }[]): string {
  const days = rows.map((row) => row.stat_date).sort()
  return rawDataLink({
    dataset: 'profile_stats_daily',
    filters: { channel: [...new Set(rows.map((row) => row.handle))] },
    from: days[0],
    to: days.at(-1),
  })
}

export function channelLifetimeLink(rows: { handle: string }[]): string {
  return rawDataLink({
    dataset: 'profile_stats_lifetime',
    filters: { channel: [...new Set(rows.map((row) => row.handle))] },
  })
}
